import React, { useEffect, useRef } from 'react';

/**
 * AnimatedBackground — light pastel "liquid / smoke" canvas effect.
 *
 * Soft radial blobs are composited with 'lighter' so the colors melt into each
 * other like dye in water. Each blob eases toward the cursor (double-smoothed
 * spring) so the whole field sways toward the mouse with a weighty, fluid
 * delay. On low-power setups it reduces the blob count and DPR; on
 * prefers-reduced-motion it renders a single static frame.
 */
const BASE_COLORS = [
  { c: [129, 140, 248], a: 0.34 }, // indigo
  { c: [96, 165, 250], a: 0.30 },  // blue
  { c: [45, 212, 191], a: 0.26 },  // teal
  { c: [251, 191, 36], a: 0.18 },  // amber
  { c: [244, 114, 182], a: 0.20 }, // pink
  { c: [167, 139, 250], a: 0.30 }, // violet
];

function makeBlob(width, height) {
  const color = BASE_COLORS[Math.floor(Math.random() * BASE_COLORS.length)];
  const baseX = Math.random() * width;
  const baseY = Math.random() * height;
  return {
    baseX,
    baseY,
    x: baseX,
    y: baseY,
    vx: 0,
    vy: 0,
    color,
    rFactor: 0.32 + Math.random() * 0.2,
    strength: 0.5 + Math.random() * 0.5,
    phase: Math.random() * Math.PI * 2,
    speed: 0.12 + Math.random() * 0.18,
  };
}

export default function AnimatedBackground({ className = '' }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const lowPower = (navigator.hardwareConcurrency || 8) <= 4;

    let raf = 0;
    let width = 0;
    let height = 0;
    const mouse = { x: 0, y: 0, active: false };
    const blobs = [];

    const blobCount = () => (width < 768 ? 4 : lowPower ? 4 : 6);

    function resize() {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const target = blobCount();
      while (blobs.length < target) blobs.push(makeBlob(width, height));
      blobs.length = target;
    }

    function draw(t) {
      const time = t * 0.001;

      const bg = ctx.createLinearGradient(0, 0, width, height);
      bg.addColorStop(0, '#eef2ff');
      bg.addColorStop(0.5, '#f9fafb');
      bg.addColorStop(1, '#ecfdf5');
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      const attractX = mouse.active
        ? mouse.x
        : width * 0.5 + Math.sin(time * 0.3) * width * 0.08;
      const attractY = mouse.active
        ? mouse.y
        : height * 0.5 + Math.cos(time * 0.24) * height * 0.06;

      ctx.globalCompositeOperation = 'lighter';
      for (const b of blobs) {
        const driftX = Math.sin(time * b.speed + b.phase) * 46;
        const driftY = Math.cos(time * b.speed * 0.8 + b.phase) * 38;

        const tx = b.baseX + driftX + (attractX - b.baseX) * b.strength * 0.2;
        const ty = b.baseY + driftY + (attractY - b.baseY) * b.strength * 0.2;

        // damped spring — gives the heavy, fluid delay
        b.vx += (tx - b.x) * 0.014;
        b.vy += (ty - b.y) * 0.014;
        b.vx *= 0.9;
        b.vy *= 0.9;
        b.x += b.vx;
        b.y += b.vy;

        const radius = Math.max(130, Math.min(width, height) * b.rFactor);
        const grad = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, radius);
        const [r, g, bl] = b.color.c;
        grad.addColorStop(0, `rgba(${r},${g},${bl},${b.color.a})`);
        grad.addColorStop(0.55, `rgba(${r},${g},${bl},${b.color.a * 0.32})`);
        grad.addColorStop(1, `rgba(${r},${g},${bl},0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(b.x, b.y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    function loop(t) {
      draw(t);
      raf = requestAnimationFrame(loop);
    }

    function onMove(e) {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      mouse.active = true;
    }

    function onLeave() {
      mouse.active = false;
    }

    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseleave', onLeave);

    if (prefersReduced) {
      draw(0);
    } else {
      raf = requestAnimationFrame(loop);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 ${className}`}
    />
  );
}
